import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Plus, Trash2, Edit, Paperclip, Download,
  Wallet, Search, X, FileText, Save, Building2, FileSpreadsheet,
  Undo2, HandCoins, Banknote, PieChart as PieIcon,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { API_URL } from "../../config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
} from "recharts";

import { FinancialSummary } from "./FinancialSummary";

const fmt = (n) => `${Number(n || 0).toFixed(2)}€`;

// ── PayeeAutocomplete ────────────────────────────────────────────────────────
// Input with case-insensitive live filtering of existing payees + inline
// "Guardar na lista" action when the typed value doesn't exist yet.
function PayeeAutocomplete({ value, onChange, payees, kind, onPayeeCreated, placeholder }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const query = (value || "").trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return payees.slice(0, 8);
    return payees.filter((p) => (p.name || "").toLowerCase().includes(query)).slice(0, 8);
  }, [payees, query]);

  const exactMatch = payees.some((p) => (p.name || "").toLowerCase() === query);
  const canOfferSave = query.length >= 2 && !exactMatch;

  const savePayee = async () => {
    if (!canOfferSave) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/finance/payees`,
        { name: value.trim(), kind: kind || "expense" },
        { withCredentials: true },
      );
      toast.success(`Entidade "${data.name}" guardada na lista`);
      onPayeeCreated && onPayeeCreated(data);
      onChange(data.name); // normalize
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a guardar entidade");
    } finally { setSaving(false); }
  };

  return (
    <div ref={containerRef} className="relative mt-1.5">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="input-dark"
        placeholder={placeholder}
        data-testid="entry-counterparty"
        autoComplete="off"
      />
      {open && (matches.length > 0 || canOfferSave) && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-zinc-900 shadow-xl" data-testid="payee-suggestions">
          {matches.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => { onChange(p.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-white/[0.05] flex items-center justify-between gap-2"
              data-testid={`payee-suggestion-${p.id}`}
            >
              <span className="text-white text-sm truncate flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                {p.name}
              </span>
              {p.tax_id && <span className="text-[10px] text-zinc-500 font-mono shrink-0">NIF {p.tax_id}</span>}
            </button>
          ))}
          {canOfferSave && (
            <button
              type="button"
              onClick={savePayee}
              disabled={saving}
              className="w-full text-left px-3 py-2 hover:bg-sky-500/10 flex items-center gap-2 border-t border-white/5 text-sky-300 text-sm"
              data-testid="payee-save-new"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "A guardar…" : <>Guardar <strong className="text-white">&ldquo;{value.trim()}&rdquo;</strong> na lista para reutilizar</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── EntryFormDialog ──────────────────────────────────────────────────────────
function EntryFormDialog({ open, onOpenChange, kind, editing, prefill, categories, methods, payees = [], onPayeeCreated, onSaved }) {
  const empty = {
    amount: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    description: "",
    counterparty: "",
    category_id: "",
    payment_method_id: "",
    person: "",
    buyer: "",
    notes: "",
  };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        amount: editing.amount ?? "",
        invoice_date: editing.invoice_date || empty.invoice_date,
        description: editing.description || "",
        counterparty: editing.counterparty || "",
        category_id: editing.category_id || "",
        payment_method_id: editing.payment_method_id || "",
        person: editing.person || "",
        buyer: editing.buyer || "",
        notes: editing.notes || "",
      });
    } else if (open) {
      setForm({ ...empty, ...(prefill || {}) });
    }
    setFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, prefill]);

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Valor inválido"); return; }
    if (!form.invoice_date) { toast.error("Data é obrigatória"); return; }
    setBusy(true);
    try {
      const payload = {
        kind,
        amount,
        invoice_date: form.invoice_date,
        description: form.description,
        counterparty: form.counterparty,
        category_id: form.category_id || null,
        payment_method_id: form.payment_method_id || null,
        person: form.person,
        buyer: form.buyer,
        notes: form.notes,
      };
      let entryId = editing?.id;
      if (editing) {
        await axios.put(`${API_URL}/api/admin/finance/entries/${entryId}`, payload, { withCredentials: true });
      } else {
        const { data } = await axios.post(`${API_URL}/api/admin/finance/entries`, payload, { withCredentials: true });
        entryId = data.id;
      }
      if (file && entryId) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await axios.post(`${API_URL}/api/admin/finance/entries/${entryId}/invoice`, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error("Entrada guardada, mas falhou upload da fatura: " + (err.response?.data?.detail || ""));
        }
      }
      toast.success(editing ? "Entrada atualizada" : "Entrada criada");
      onSaved && onSaved({ id: entryId, ...payload });
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a guardar");
    } finally { setBusy(false); }
  };

  const isIncome = kind === "income";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIncome ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
            {editing ? `Editar ${isIncome ? "Receita" : "Despesa"}` : `Nova ${isIncome ? "Receita" : "Despesa"}`}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Preencha os detalhes da {isIncome ? "receita" : "despesa"}. Pode anexar a fatura em PDF ou imagem.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-zinc-300">Valor (€)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-dark mt-1.5" data-testid="entry-amount" />
          </div>
          <div>
            <Label className="text-zinc-300">{isIncome ? "Data da Receita" : "Data da Fatura"}</Label>
            <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="input-dark mt-1.5" data-testid="entry-date" />
          </div>
          <div className="col-span-2">
            <Label className="text-zinc-300">Descrição</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark mt-1.5" data-testid="entry-description" />
          </div>
          <div className="col-span-2">
            <Label className="text-zinc-300">{isIncome ? "Recebido de" : "Pago a Entidade"}</Label>
            <PayeeAutocomplete
              value={form.counterparty}
              onChange={(v) => setForm({ ...form, counterparty: v })}
              payees={payees}
              kind={kind}
              onPayeeCreated={onPayeeCreated}
              placeholder={payees.length ? "Começa a escrever ou escolhe da lista…" : "Escreve o nome da entidade…"}
            />
            {payees.length === 0 && (
              <p className="text-[11px] text-zinc-500 mt-1">
                💡 Podes gerir a Lista de Entidades em <strong>Configurações → Financeira</strong> para ter sugestões automáticas.
              </p>
            )}
          </div>
          <div>
            <Label className="text-zinc-300">Tipo de {isIncome ? "Receita" : "Despesa"}</Label>
            <Select value={form.category_id || "__none__"} onValueChange={(v) => setForm({ ...form, category_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="input-dark mt-1.5" data-testid="entry-category">
                <SelectValue placeholder="Selecione…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="__none__" className="text-zinc-500">(sem categoria)</SelectItem>
                {categories.filter((c) => c.active !== false).map((c) => (
                  <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300">{isIncome ? "Método de Receita" : "Método de Pagamento"}</Label>
            <Select value={form.payment_method_id || "__none__"} onValueChange={(v) => setForm({ ...form, payment_method_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="input-dark mt-1.5" data-testid="entry-method">
                <SelectValue placeholder="Selecione…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="__none__" className="text-zinc-500">(não definido)</SelectItem>
                {methods.filter((m) => m.active !== false).map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isIncome && (
            <div className="col-span-2">
              <Label className="text-zinc-300">
                COMPRADOR <span className="text-zinc-500 font-normal">(pessoa que foi às compras)</span>
              </Label>
              <Input
                value={form.buyer}
                onChange={(e) => setForm({ ...form, buyer: e.target.value })}
                className="input-dark mt-1.5"
                placeholder="Nome"
                data-testid="entry-buyer"
              />
            </div>
          )}
          <div className="col-span-2">
            <Label className="text-zinc-300">
              {isIncome
                ? <>Contabilizador <span className="text-zinc-500 font-normal">(pessoa que contou a entrada)</span></>
                : <>PAGADOR <span className="text-zinc-500 font-normal">(titular do pagamento)</span></>}
            </Label>
            <Input value={form.person} onChange={(e) => setForm({ ...form, person: e.target.value })} className="input-dark mt-1.5" placeholder="Nome" data-testid="entry-person" />
          </div>
          <div className="col-span-2">
            <Label className="text-zinc-300">Notas</Label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="input-dark mt-1.5 w-full p-2 rounded" data-testid="entry-notes" />
          </div>
          <div className="col-span-2">
            <Label className="text-zinc-300 flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              Anexar Fatura (PDF, PNG, JPG, WEBP — max 8MB)
            </Label>
            <Input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="input-dark mt-1.5"
              data-testid="entry-file"
            />
            {editing?.invoice_filename && !file && (
              <p className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Atual: {editing.invoice_filename}
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-white/10">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10 text-zinc-300">Cancelar</Button>
          <Button onClick={save} disabled={busy} className="btn-primary" data-testid="entry-save">
            {busy ? "A guardar..." : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── EntriesTab ───────────────────────────────────────────────────────────────
function EntriesTab({ kind }) {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const [payees, setPayees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Filters
  const [filterCat, setFilterCat] = useState("");
  const [filterMethod, setFilterMethod] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { kind };
      if (filterCat) params.category_id = filterCat;
      if (filterMethod) params.payment_method_id = filterMethod;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      if (search) params.q = search;
      const [eRes, cRes, mRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/finance/entries`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/categories?kind=${kind}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/payment-methods`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/payees?kind=${kind}`, { withCredentials: true }),
      ]);
      setEntries(eRes.data || []);
      setCategories(cRes.data || []);
      setMethods(mRes.data || []);
      setPayees((pRes.data || []).filter((p) => p.active !== false));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a carregar");
    } finally { setLoading(false); }
  }, [kind, filterCat, filterMethod, filterFrom, filterTo, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const total = useMemo(() => entries.reduce((acc, e) => acc + (e.amount || 0), 0), [entries]);
  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);
  const methodMap = useMemo(() => Object.fromEntries(methods.map((m) => [m.id, m.name])), [methods]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta entrada?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/finance/entries/${id}`, { withCredentials: true });
      toast.success("Entrada eliminada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const exportCSV = () => {
    const rows = [
      ["Data", "Descrição", kind === "income" ? "Recebido de" : "Pago a Entidade", "Tipo", "Método", kind === "income" ? "Contabilizou" : "Pagador", ...(kind === "expense" ? ["Comprador"] : []), "Valor", "Notas"],
      ...entries.map((e) => [
        e.invoice_date, e.description, e.counterparty,
        catMap[e.category_id] || "", methodMap[e.payment_method_id] || "",
        e.person, ...(kind === "expense" ? [e.buyer || ""] : []),
        e.amount?.toFixed(2), (e.notes || "").replace(/\n/g, " "),
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const isIncome = kind === "income";
  const accent = isIncome ? "text-emerald-400" : "text-red-400";
  const Icon = isIncome ? TrendingUp : TrendingDown;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Icon className={`w-6 h-6 ${accent}`} />
          <div>
            <h3 className="text-lg font-semibold text-white">{isIncome ? "Receitas" : "Despesas"}</h3>
            <p className="text-zinc-500 text-sm">{entries.length} entrada(s) · Total <span className={accent}>{fmt(total)}</span></p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCSV} variant="outline" className="border-white/10 text-zinc-300" data-testid={`${kind}-export`}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="btn-primary" data-testid={`${kind}-create`}>
            <Plus className="w-4 h-4 mr-2" />
            Nova {isIncome ? "Receita" : "Despesa"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-3 grid grid-cols-2 md:grid-cols-5 gap-2">
        <div>
          <Label className="text-zinc-400 text-xs">Procurar</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Descrição, pessoa..." className="input-dark pl-8 h-9" />
          </div>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Categoria</Label>
          <Select value={filterCat || "__all__"} onValueChange={(v) => setFilterCat(v === "__all__" ? "" : v)}>
            <SelectTrigger className="input-dark h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="__all__" className="text-zinc-300">Todas</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Método</Label>
          <Select value={filterMethod || "__all__"} onValueChange={(v) => setFilterMethod(v === "__all__" ? "" : v)}>
            <SelectTrigger className="input-dark h-9"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="__all__" className="text-zinc-300">Todos</SelectItem>
              {methods.map((m) => <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Desde</Label>
          <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="input-dark h-9" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Até</Label>
          <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="input-dark h-9" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider">
                <th className="px-3 py-2.5">Data</th>
                <th className="px-3 py-2.5">Descrição</th>
                <th className="px-3 py-2.5">{isIncome ? "Recebido de" : "Pago a Entidade"}</th>
                <th className="px-3 py-2.5">Tipo</th>
                <th className="px-3 py-2.5">Método</th>
                <th className="px-3 py-2.5">{isIncome ? "Contabilizou" : "Pagador"}</th>
                {!isIncome && <th className="px-3 py-2.5">Comprador</th>}
                <th className="px-3 py-2.5 text-right">Valor</th>
                <th className="px-3 py-2.5">Fatura</th>
                <th className="px-3 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isIncome ? 9 : 10} className="text-center text-zinc-500 py-8">A carregar…</td></tr>}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={isIncome ? 9 : 10} className="text-center text-zinc-500 py-8">Sem entradas</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-2 text-zinc-300">{e.invoice_date}</td>
                  <td className="px-3 py-2 text-white">{e.description || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300">{e.counterparty || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300">{catMap[e.category_id] || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300">{methodMap[e.payment_method_id] || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300">{e.person || "—"}</td>
                  {!isIncome && <td className="px-3 py-2 text-zinc-300">{e.buyer || "—"}</td>}
                  <td className={`px-3 py-2 text-right font-medium ${accent}`}>{fmt(e.amount)}</td>
                  <td className="px-3 py-2">
                    {e.invoice_url ? (
                      <div className="inline-flex items-center gap-1">
                        <a
                          href={`${API_URL}${e.invoice_url}`}
                          target="_blank"
                          rel="noreferrer"
                          title={e.invoice_filename ? `Ver ${e.invoice_filename}` : "Ver factura"}
                          className="p-1.5 rounded text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 transition-colors inline-flex items-center gap-1"
                          data-testid={`finance-invoice-view-${e.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </a>
                        <a
                          href={`${API_URL}${e.invoice_url}`}
                          download={e.invoice_filename || "factura"}
                          title={e.invoice_filename ? `Descarregar ${e.invoice_filename}` : "Descarregar factura"}
                          className="p-1.5 rounded text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-colors inline-flex items-center gap-1"
                          data-testid={`finance-invoice-download-${e.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => { setEditing(e); setDialogOpen(true); }}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(e.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EntryFormDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        kind={kind} editing={editing}
        categories={categories} methods={methods} payees={payees}
        onPayeeCreated={(p) => setPayees((prev) => [...prev, p])}
        onSaved={fetchAll}
      />
    </div>
  );
}

// ── Resumo Geral ─────────────────────────────────────────────────────────────
function SummaryCharts({ data }) {
  // 1) Composição da Receita Total (Donut)
  const receitaData = useMemo(() => ([
    { name: "Receitas Manuais", value: Number(data.income_manual) || 0, color: "#10b981" },
    { name: "Vendas (Bilhetes)", value: Number(data.ticket_sales) || 0, color: "#8b5cf6" },
  ].filter((d) => d.value > 0)), [data]);

  // 2) Composição das Despesas Líquidas (Donut) — Despesas brutas − Devoluções
  const despesasData = useMemo(() => {
    const bruto = Number(data.expense_total) || 0;
    const devol = Number(data.refunds_total) || 0;
    return [
      { name: "Despesas Efectivas", value: Math.max(0, bruto - devol), color: "#ef4444" },
      { name: "Devoluções (abatem)", value: devol, color: "#f97316" },
    ].filter((d) => d.value > 0);
  }, [data]);

  // 3) Fluxo Financeiro (Bar) — Receita vs Despesas Líq. vs Compromissos vs Saldo Ajustado
  const fluxoData = useMemo(() => ([
    { nome: "Receita Total",     valor: Number(data.income_total) || 0,     color: "#10b981" },
    { nome: "Despesas Líquidas", valor: Number(data.expenses_net) || 0,     color: "#ef4444" },
    { nome: "Compromissos",      valor: Number(data.compromissos) || 0,     color: "#0ea5e9" },
    { nome: "Saldo Ajustado",    valor: Number(data.saldo_ajustado) || 0,   color: (Number(data.saldo_ajustado) || 0) >= 0 ? "#f59e0b" : "#ef4444" },
  ]), [data]);

  // 4) Compromissos (Donut) — Credores vs Adiantados
  const compromissosData = useMemo(() => ([
    { name: "Credores em Aberto",   value: Number(data.creditors_pending) || 0, color: "#0ea5e9" },
    { name: "Adiantados em Aberto", value: Number(data.advances_open) || 0,     color: "#eab308" },
  ].filter((d) => d.value > 0)), [data]);

  const tooltipStyle = {
    backgroundColor: "#18181b", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px", color: "#fff", fontSize: 12,
  };
  const emptyMsg = <div className="h-[220px] flex items-center justify-center text-zinc-600 text-sm">Sem dados</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="summary-charts">
      {/* Receita */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Composição da Receita Total</h4>
          <span className="text-xs text-zinc-500">{fmt(data.income_total)}</span>
        </div>
        {receitaData.length === 0 ? emptyMsg : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={receitaData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {receitaData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <RTooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Despesas líquidas */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Composição das Despesas Líquidas</h4>
          <span className="text-xs text-zinc-500">{fmt(data.expenses_net)}</span>
        </div>
        {despesasData.length === 0 ? emptyMsg : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={despesasData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {despesasData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <RTooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Fluxo Financeiro */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Fluxo Financeiro</h4>
          <span className="text-xs text-zinc-500">Receita → Despesas → Compromissos → Saldo Ajustado</span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={fluxoData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="nome" tick={{ fill: "#a1a1aa", fontSize: 11 }} />
            <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
            <RTooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
            <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
              {fluxoData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Compromissos */}
      {compromissosData.length > 0 && (
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-white">Compromissos em Aberto</h4>
            <span className="text-xs text-zinc-500">{fmt(data.compromissos)}</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={compromissosData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {compromissosData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <RTooltip contentStyle={tooltipStyle} formatter={(v) => fmt(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function SummaryTab() {
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API_URL}/api/admin/finance/summary`, { params, withCredentials: true });
      setData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  }, [dateFrom, dateTo]);
  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return <p className="text-zinc-500">A carregar…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <PieIcon className="w-6 h-6 text-amber-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Resumo</h3>
          <p className="text-zinc-500 text-sm">Visão geral do estado financeiro · Entradas ({fmt(data.income_total)}) − Saídas Líquidas ({fmt(data.expenses_net)}) − Compromissos ({fmt(data.compromissos)}) = <span className={data.saldo_ajustado >= 0 ? "text-amber-300" : "text-red-300"}>Saldo Ajustado {fmt(data.saldo_ajustado)}</span></p>
        </div>
      </div>
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-3 grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label className="text-zinc-400 text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-dark h-9" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Até</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-dark h-9" />
        </div>
      </div>
      {/* Linha 1 — Saldos e Entradas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`rounded-xl p-5 border ${data.saldo >= 0 ? "bg-amber-500/10 border-amber-500/30" : "bg-red-500/10 border-red-500/30"}`}>
          <p className={`text-xs uppercase tracking-wider font-semibold ${data.saldo >= 0 ? "text-amber-300" : "text-red-300"}`}>Saldo</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.saldo)}</p>
          <p className="text-xs text-zinc-500 mt-1">Receita total − Despesas</p>
        </div>
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-5">
          <p className="text-violet-300 text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Vendas (Bilhetes)</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.ticket_sales)}</p>
          <p className="text-xs text-zinc-500 mt-1">Agregado (não duplicado)</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
          <p className="text-emerald-300 text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Receitas Manuais</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.income_manual)}</p>
        </div>
        <div className={`rounded-xl p-5 border ${data.saldo_ajustado >= 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
          <p className={`text-xs uppercase tracking-wider font-semibold ${data.saldo_ajustado >= 0 ? "text-emerald-300" : "text-red-300"}`}>Saldo Ajustado</p>
          <p className="text-3xl font-bold text-white mt-2">{fmt(data.saldo_ajustado)}</p>
          <p className="text-xs text-zinc-500 mt-1">Saldo − Compromissos ({fmt(data.compromissos)})</p>
        </div>
      </div>

      {/* Fórmula das Receitas */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <p className="text-zinc-400 text-sm">
          <span className="text-white font-medium">Receita Total: {fmt(data.income_total)}</span> = Receitas manuais ({fmt(data.income_manual)}) + Vendas de bilhetes ({fmt(data.ticket_sales)})
        </p>
      </div>

      {/* Linha 2 — Despesas e Saídas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
          <p className="text-red-300 text-xs uppercase tracking-wider font-semibold flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5" /> Despesas</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.expense_total)}</p>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5">
          <p className="text-orange-300 text-xs uppercase tracking-wider font-semibold">Devoluções</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.refunds_total)}</p>
          <p className="text-xs text-zinc-500 mt-1">Reduzem as despesas</p>
        </div>
        <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-5">
          <p className="text-sky-300 text-xs uppercase tracking-wider font-semibold">Credores em Aberto</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.creditors_pending)}</p>
          <p className="text-xs text-zinc-500 mt-1">Dívidas por pagar</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5">
          <p className="text-yellow-300 text-xs uppercase tracking-wider font-semibold">Adiantados em Aberto</p>
          <p className="text-2xl font-bold text-white mt-2">{fmt(data.advances_open)}</p>
          <p className="text-xs text-zinc-500 mt-1">Por justificar / devolver</p>
        </div>
      </div>

      {/* Fórmula das Despesas Líquidas */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <p className="text-zinc-400 text-sm">
          <span className="text-white font-medium">Despesas Líquidas: {fmt(data.expenses_net)}</span> = Despesas ({fmt(data.expense_total)}) − Devoluções ({fmt(data.refunds_total)})
        </p>
      </div>

      {/* Gráficos visuais */}
      <SummaryCharts data={data} />

      {/* Export */}
      <div className="flex justify-end gap-2 pt-2">
        <Button size="sm" onClick={() => exportSummaryExcel(data, dateFrom, dateTo)} className="btn-secondary" data-testid="summary-export-excel">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
        </Button>
        <Button size="sm" onClick={() => exportSummaryPDF(data, dateFrom, dateTo)} className="btn-secondary" data-testid="summary-export-pdf">
          <FileText className="w-4 h-4 mr-2" /> PDF
        </Button>
      </div>
    </div>
  );
}

// ── Export do RESUMO ─────────────────────────────────────────────────────────
function exportSummaryExcel(data, from, to) {
  const range = (from || to) ? `${from || "início"} a ${to || "hoje"}` : "Total";
  const rows = [
    ["RESUMO FINANCEIRO", `Período: ${range}`, `Exportado em ${new Date().toLocaleString("pt-PT")}`],
    [],
    ["Métrica", "Valor (€)"],
    ["Receitas Manuais", data.income_manual],
    ["Vendas (Bilhetes)", data.ticket_sales],
    ["Receita Total", data.income_total],
    ["Despesas", data.expense_total],
    ["Devoluções", data.refunds_total],
    ["Despesas Líquidas", data.expenses_net],
    ["Saldo (Receita − Despesas Líquidas)", data.saldo],
    [],
    ["Credores em Aberto", data.creditors_pending],
    ["Adiantados em Aberto", data.advances_open],
    ["Compromissos", data.compromissos],
    ["SALDO AJUSTADO", data.saldo_ajustado],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [{ wch: 44 }, { wch: 18 }, { wch: 26 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Resumo");
  XLSX.writeFile(wb, `resumo_financeiro_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function exportSummaryPDF(data, from, to) {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const range = (from || to) ? `${from || "início"} a ${to || "hoje"}` : "Total";
  doc.setFontSize(16); doc.text("Resumo Financeiro", 40, 40);
  doc.setFontSize(10); doc.setTextColor(80);
  doc.text(`Período: ${range}`, 40, 60);
  doc.text(`Exportado em: ${new Date().toLocaleString("pt-PT")}`, 40, 74);
  const body = [
    ["Receitas Manuais", fmt(data.income_manual)],
    ["Vendas (Bilhetes)", fmt(data.ticket_sales)],
    ["Receita Total", fmt(data.income_total)],
    ["Despesas", fmt(data.expense_total)],
    ["Devoluções", fmt(data.refunds_total)],
    ["Despesas Líquidas", fmt(data.expenses_net)],
    ["Saldo", fmt(data.saldo)],
    ["Credores em Aberto", fmt(data.creditors_pending)],
    ["Adiantados em Aberto", fmt(data.advances_open)],
    ["Compromissos", fmt(data.compromissos)],
    ["SALDO AJUSTADO", fmt(data.saldo_ajustado)],
  ];
  autoTable(doc, { startY: 94, head: [["Métrica", "Valor"]], body,
    styles: { fontSize: 10, cellPadding: 6 }, headStyles: { fillColor: [24, 24, 27], textColor: 255 },
    columnStyles: { 0: { cellWidth: 340 }, 1: { cellWidth: 140, halign: "right" } }, theme: "grid" });
  doc.save(`resumo_financeiro_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── RefundsTab / CreditorsTab / AdvancesTab (Sprint 13) ──────────────────────
function RefundsTab() {
  const [items, setItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");
  const [related, setRelated] = useState("");
  const [person, setPerson] = useState("");
  const load = useCallback(async () => {
    const [r, e] = await Promise.all([
      axios.get(`${API_URL}/api/admin/finance/refunds`, { withCredentials: true }),
      axios.get(`${API_URL}/api/admin/finance/entries?kind=expense`, { withCredentials: true }),
    ]);
    setItems(r.data || []); setExpenses(e.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Valor inválido");
    try {
      await axios.post(`${API_URL}/api/admin/finance/refunds`, {
        amount: Number(amount), date, description,
        related_expense_id: related || null, person,
      }, { withCredentials: true });
      setAmount(""); setDescription(""); setRelated(""); setPerson("");
      toast.success("Devolução criada"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminar esta devolução?")) return;
    try { await axios.delete(`${API_URL}/api/admin/finance/refunds/${id}`, { withCredentials: true }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const [editing, setEditing] = useState(null); // {id, date, description, person, related_expense_id, amount}
  const startEdit = (r) => setEditing({
    id: r.id, date: r.date || "", description: r.description || "",
    person: r.person || "", related_expense_id: r.related_expense_id || "",
    amount: r.amount ?? "",
  });
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/admin/finance/refunds/${editing.id}`, {
        date: editing.date,
        description: editing.description,
        person: editing.person,
        related_expense_id: editing.related_expense_id || null,
        amount: Number(editing.amount) || 0,
      }, { withCredentials: true });
      toast.success("Devolução actualizada");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const totalRefunds = useMemo(() => items.reduce((s, r) => s + (Number(r.amount) || 0), 0), [items]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Undo2 className="w-6 h-6 text-orange-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Devoluções</h3>
          <p className="text-zinc-500 text-sm">{items.length} devolução(ões) · Total <span className="text-orange-300">{fmt(totalRefunds)}</span> · Reduzem as Despesas Líquidas no cálculo do Saldo</p>
        </div>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-[100px_130px_1fr_1fr_1fr_60px] gap-2 items-end">
        <div><Label className="text-zinc-400 text-xs">Valor (€)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark h-9" data-testid="refund-amount" /></div>
        <div><Label className="text-zinc-400 text-xs">Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-dark h-9" /></div>
        <div><Label className="text-zinc-400 text-xs">Descrição</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} className="input-dark h-9" placeholder="Ex: Devolução de material" data-testid="refund-desc" /></div>
        <div><Label className="text-zinc-400 text-xs">Despesa associada (opcional)</Label>
          <select value={related} onChange={(e) => setRelated(e.target.value)} className="bg-zinc-800 border border-white/10 rounded-md text-white px-2 h-9 w-full text-sm">
            <option value="">— nenhuma —</option>
            {expenses.map((x) => <option key={x.id} value={x.id}>{x.invoice_date} · {x.description || x.counterparty || "?"} ({x.amount?.toFixed(2)}€)</option>)}
          </select>
        </div>
        <div><Label className="text-zinc-400 text-xs">Pessoa</Label><Input value={person} onChange={(e) => setPerson(e.target.value)} className="input-dark h-9" /></div>
        <Button onClick={add} className="btn-primary h-9" data-testid="refund-add"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-zinc-400"><tr><th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-left">Descrição</th><th className="px-3 py-2 text-left">Pessoa</th><th className="px-3 py-2 text-left">Despesa Assoc.</th><th className="px-3 py-2 text-right">Valor</th><th /></tr></thead>
          <tbody>{items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Sem devoluções</td></tr>}
            {items.map((r) => {
              const e = expenses.find((x) => x.id === r.related_expense_id);
              const isEdit = editing?.id === r.id;
              if (isEdit) {
                return (
                  <tr key={r.id} className="border-t border-white/5 bg-white/[0.03]">
                    <td className="px-3 py-2"><Input type="date" value={editing.date} onChange={(ev) => setEditing({ ...editing, date: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2"><Input value={editing.description} onChange={(ev) => setEditing({ ...editing, description: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2"><Input value={editing.person} onChange={(ev) => setEditing({ ...editing, person: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2">
                      <select value={editing.related_expense_id} onChange={(ev) => setEditing({ ...editing, related_expense_id: ev.target.value })} className="bg-zinc-800 border border-white/10 rounded-md text-white px-2 h-8 w-full text-xs">
                        <option value="">— nenhuma —</option>
                        {expenses.map((x) => <option key={x.id} value={x.id}>{x.invoice_date} · {x.description || x.counterparty || "?"} ({x.amount?.toFixed(2)}€)</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2"><Input type="number" step="0.01" value={editing.amount} onChange={(ev) => setEditing({ ...editing, amount: ev.target.value })} className="input-dark h-8 text-right" /></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" onClick={saveEdit} className="bg-emerald-500 hover:bg-emerald-600 h-8" data-testid={`refund-save-${r.id}`}><Save className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 h-8"><X className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              }
              return (<tr key={r.id} className="border-t border-white/5"><td className="px-3 py-2 text-zinc-300">{r.date}</td><td className="px-3 py-2 text-white">{r.description || "—"}</td><td className="px-3 py-2 text-zinc-300">{r.person || "—"}</td><td className="px-3 py-2 text-zinc-400 text-xs">{e ? `${e.description || e.counterparty} (${e.amount?.toFixed(2)}€)` : "—"}</td><td className="px-3 py-2 text-right text-orange-300 font-medium">{fmt(r.amount)}</td><td className="px-3 py-2 text-right whitespace-nowrap"><Button size="sm" variant="ghost" onClick={() => startEdit(r)} className="text-zinc-300" data-testid={`refund-edit-${r.id}`}><Edit className="w-4 h-4" /></Button><Button size="sm" variant="ghost" onClick={() => del(r.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button></td></tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreditorsTab() {
  const [items, setItems] = useState([]);
  const [person, setPerson] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  // Support data for the "Registar como despesa" flow (pre-filled EntryFormDialog)
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const [payees, setPayees] = useState([]);
  const [payDialog, setPayDialog] = useState(null); // {creditor} — asks the choice
  const [expenseDialog, setExpenseDialog] = useState({ open: false, prefill: null, creditorId: null });

  const load = useCallback(async () => {
    const [cRes, cats, mets, pyz] = await Promise.all([
      axios.get(`${API_URL}/api/admin/finance/creditors`, { withCredentials: true }),
      axios.get(`${API_URL}/api/admin/finance/categories?kind=expense`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/admin/finance/payment-methods`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/admin/finance/payees?kind=expense`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]);
    setItems(cRes.data || []);
    setCategories(cats.data || []);
    setMethods(mets.data || []);
    setPayees(pyz.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!person.trim() || !amount || Number(amount) <= 0) return toast.error("Preenche nome e valor");
    try {
      await axios.post(`${API_URL}/api/admin/finance/creditors`, {
        person: person.trim(), amount: Number(amount), date_incurred: date, reason,
      }, { withCredentials: true });
      setPerson(""); setAmount(""); setReason("");
      toast.success("Credor adicionado"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const markPaidOnly = async (id, expenseId = null) => {
    try {
      const payload = expenseId ? { related_expense_id: expenseId } : {};
      await axios.post(`${API_URL}/api/admin/finance/creditors/${id}/mark-paid`, payload, { withCredentials: true });
      toast.success(expenseId ? "Credor pago e Despesa registada" : "Marcado como pago");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const openPayDialog = (c) => setPayDialog({ creditor: c });
  const openExpenseFlow = (c) => {
    setPayDialog(null);
    setExpenseDialog({
      open: true,
      creditorId: c.id,
      prefill: {
        amount: c.amount ?? "",
        invoice_date: new Date().toISOString().slice(0, 10),
        description: `Pagamento a credor: ${c.person} · ${c.reason || "sem descrição"}`,
        counterparty: c.person || "",
        notes: "[Pagamento a credor]",
      },
    });
  };
  const onExpenseSaved = async (entry) => {
    // Called by EntryFormDialog after the expense is persisted
    const { creditorId } = expenseDialog;
    setExpenseDialog({ open: false, prefill: null, creditorId: null });
    if (creditorId && entry?.id) await markPaidOnly(creditorId, entry.id);
  };
  const del = async (id) => {
    if (!window.confirm("Eliminar credor?")) return;
    try { await axios.delete(`${API_URL}/api/admin/finance/creditors/${id}`, { withCredentials: true }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const [editing, setEditing] = useState(null); // {id, date_incurred, person, reason, amount}
  const startEdit = (c) => setEditing({
    id: c.id, date_incurred: c.date_incurred || "", person: c.person || "",
    reason: c.reason || "", amount: c.amount ?? "",
  });
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/admin/finance/creditors/${editing.id}`, {
        date_incurred: editing.date_incurred,
        person: editing.person,
        reason: editing.reason,
        amount: Number(editing.amount) || 0,
      }, { withCredentials: true });
      toast.success("Credor actualizado");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const totalPendingCreditors = useMemo(
    () => items.filter((c) => c.status !== "paid").reduce((s, c) => s + (Number(c.amount) || 0), 0),
    [items],
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <HandCoins className="w-6 h-6 text-sky-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Credores</h3>
          <p className="text-zinc-500 text-sm">{items.length} registo(s) · Em aberto <span className="text-sky-300">{fmt(totalPendingCreditors)}</span> · Dívidas da organização a alguém (ex: adiantou do bolso e ainda não foi reembolsado)</p>
        </div>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_100px_130px_1fr_60px] gap-2 items-end">
        <div><Label className="text-zinc-400 text-xs">Credor</Label><Input value={person} onChange={(e) => setPerson(e.target.value)} className="input-dark h-9" placeholder="Nome" data-testid="creditor-person" /></div>
        <div><Label className="text-zinc-400 text-xs">Valor (€)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark h-9" data-testid="creditor-amount" /></div>
        <div><Label className="text-zinc-400 text-xs">Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-dark h-9" /></div>
        <div><Label className="text-zinc-400 text-xs">Motivo</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="input-dark h-9" placeholder="Ex: Adiantou material" /></div>
        <Button onClick={add} className="btn-primary h-9" data-testid="creditor-add"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-zinc-400"><tr><th className="px-3 py-2 text-left">Data</th><th className="px-3 py-2 text-left">Credor</th><th className="px-3 py-2 text-left">Motivo</th><th className="px-3 py-2 text-right">Valor</th><th className="px-3 py-2 text-center">Estado</th><th /></tr></thead>
          <tbody>{items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-6">Sem credores</td></tr>}
            {items.map((c) => {
              const isEdit = editing?.id === c.id;
              if (isEdit) {
                return (
                  <tr key={c.id} className="border-t border-white/5 bg-white/[0.03]">
                    <td className="px-3 py-2"><Input type="date" value={editing.date_incurred} onChange={(ev) => setEditing({ ...editing, date_incurred: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2"><Input value={editing.person} onChange={(ev) => setEditing({ ...editing, person: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2"><Input value={editing.reason} onChange={(ev) => setEditing({ ...editing, reason: ev.target.value })} className="input-dark h-8" /></td>
                    <td className="px-3 py-2"><Input type="number" step="0.01" value={editing.amount} onChange={(ev) => setEditing({ ...editing, amount: ev.target.value })} className="input-dark h-8 text-right" /></td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.status === "paid" ? "Pago" : "Pendente"}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" onClick={saveEdit} className="bg-emerald-500 hover:bg-emerald-600 h-8" data-testid={`creditor-save-${c.id}`}><Save className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 h-8"><X className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                );
              }
              return (<tr key={c.id} className="border-t border-white/5"><td className="px-3 py-2 text-zinc-300">{c.date_incurred}</td><td className="px-3 py-2 text-white">{c.person}</td><td className="px-3 py-2 text-zinc-400">{c.reason || "—"}</td><td className="px-3 py-2 text-right text-sky-300 font-medium">{fmt(c.amount)}</td><td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>{c.status === "paid" ? "Pago" : "Pendente"}</span></td><td className="px-3 py-2 text-right space-x-1"><Button size="sm" variant="ghost" onClick={() => startEdit(c)} className="text-zinc-300" title="Editar" data-testid={`creditor-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>{c.status !== "paid" && <Button size="sm" variant="ghost" onClick={() => openPayDialog(c)} className="text-emerald-400" title="Marcar como pago" data-testid={`creditor-pay-${c.id}`}><Save className="w-4 h-4" /></Button>}<Button size="sm" variant="ghost" onClick={() => del(c.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button></td></tr>);
            })}
          </tbody>
        </table>
      </div>

      {/* Diálogo de escolha ao marcar como pago */}
      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white" data-testid="creditor-pay-dialog">
          <DialogHeader>
            <DialogTitle>Marcar credor como pago</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {payDialog && (<>
                <strong className="text-white">{payDialog.creditor.person}</strong> · <span className="text-sky-300 font-mono">{fmt(payDialog.creditor.amount)}</span>
                <br />
                Como queres registar este pagamento?
              </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
              <p className="text-sm text-white font-medium">Registar como Despesa <span className="text-emerald-300 text-xs ml-2">(recomendado)</span></p>
              <p className="text-xs text-zinc-500 mt-1">Abre o formulário de Nova Despesa pré-preenchido para escolheres a Categoria e o Método de Pagamento. A Despesa fica linkada ao credor e o Saldo reflecte o dinheiro que saiu.</p>
              <Button size="sm" className="btn-primary mt-2" onClick={() => openExpenseFlow(payDialog.creditor)} data-testid="creditor-pay-with-expense">
                <TrendingDown className="w-4 h-4 mr-2" /> Abrir Nova Despesa
              </Button>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
              <p className="text-sm text-white font-medium">Apenas marcar como pago</p>
              <p className="text-xs text-zinc-500 mt-1">Só actualiza o estado do credor. Não cria uma Despesa — use apenas se o pagamento já foi contabilizado noutro lado.</p>
              <Button size="sm" variant="outline" className="border-white/10 text-zinc-200 mt-2" onClick={() => { markPaidOnly(payDialog.creditor.id); setPayDialog(null); }} data-testid="creditor-pay-only">
                <Save className="w-4 h-4 mr-2" /> Só marcar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Formulário de Despesa pré-preenchido (chama mark-paid + link após guardar) */}
      <EntryFormDialog
        open={expenseDialog.open}
        onOpenChange={(o) => !o && setExpenseDialog({ open: false, prefill: null, creditorId: null })}
        kind="expense"
        editing={null}
        prefill={expenseDialog.prefill}
        categories={categories}
        methods={methods}
        payees={payees}
        onPayeeCreated={(p) => setPayees((prev) => [...prev, p])}
        onSaved={onExpenseSaved}
      />
    </div>
  );
}

function AdvancesTab() {
  const [items, setItems] = useState([]);
  const [person, setPerson] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [purpose, setPurpose] = useState("");
  const [justifyingId, setJustifyingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [jAmount, setJAmount] = useState("");
  const [jDesc, setJDesc] = useState("");
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/finance/advances`, { withCredentials: true });
    setItems(data || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!person.trim() || !amount || Number(amount) <= 0) return toast.error("Preenche nome e valor");
    try {
      await axios.post(`${API_URL}/api/admin/finance/advances`, {
        person: person.trim(), amount_given: Number(amount), date_given: date, purpose,
      }, { withCredentials: true });
      setPerson(""); setAmount(""); setPurpose("");
      toast.success("Adiantamento criado (despesa lançada automaticamente)"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const justify = async (id) => {
    if (!jAmount || Number(jAmount) <= 0) return toast.error("Valor da justificação inválido");
    try {
      await axios.post(`${API_URL}/api/admin/finance/advances/${id}/justify`, {
        amount: Number(jAmount), description: jDesc,
      }, { withCredentials: true });
      setJAmount(""); setJDesc(""); setJustifyingId(null);
      toast.success("Justificação adicionada"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const close = async (id, outstanding) => {
    const ret = window.prompt(`Valor devolvido em dinheiro (0 se tudo justificado, restante ${outstanding.toFixed(2)}€):`, String(outstanding.toFixed(2)));
    if (ret === null) return;
    try {
      await axios.post(`${API_URL}/api/admin/finance/advances/${id}/close`, {
        returned_amount: Number(ret) || 0,
      }, { withCredentials: true });
      toast.success("Adiantamento fechado"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminar adiantamento (e despesa associada)?")) return;
    try { await axios.delete(`${API_URL}/api/admin/finance/advances/${id}`, { withCredentials: true }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const totalAdvancesOpen = useMemo(() => {
    let s = 0;
    for (const a of items) {
      if (a.status !== "open") continue;
      const just = (a.justifications || []).reduce((x, j) => x + (Number(j.amount) || 0), 0);
      s += Math.max(0, (Number(a.amount_given) || 0) - just - (Number(a.returned_amount) || 0));
    }
    return s;
  }, [items]);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Banknote className="w-6 h-6 text-yellow-400" />
        <div>
          <h3 className="text-lg font-semibold text-white">Adiantados</h3>
          <p className="text-zinc-500 text-sm">{items.length} adiantamento(s) · Em aberto <span className="text-yellow-300">{fmt(totalAdvancesOpen)}</span> · Entrega de dinheiro para compras — lança automaticamente uma Despesa; justificações documentam o uso</p>
        </div>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-[1fr_100px_130px_1fr_60px] gap-2 items-end">
        <div><Label className="text-zinc-400 text-xs">Pessoa</Label><Input value={person} onChange={(e) => setPerson(e.target.value)} className="input-dark h-9" data-testid="advance-person" /></div>
        <div><Label className="text-zinc-400 text-xs">Valor (€)</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark h-9" data-testid="advance-amount" /></div>
        <div><Label className="text-zinc-400 text-xs">Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-dark h-9" /></div>
        <div><Label className="text-zinc-400 text-xs">Motivo</Label><Input value={purpose} onChange={(e) => setPurpose(e.target.value)} className="input-dark h-9" placeholder="Ex: Compras evento" /></div>
        <Button onClick={add} className="btn-primary h-9" data-testid="advance-add"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="bg-zinc-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-zinc-400"><tr>
            <th className="px-3 py-2 text-left">Data</th>
            <th className="px-3 py-2 text-left">Pessoa</th>
            <th className="px-3 py-2 text-left">Motivo</th>
            <th className="px-3 py-2 text-right">Entregue</th>
            <th className="px-3 py-2 text-right">Justificado</th>
            <th className="px-3 py-2 text-right">Devolvido</th>
            <th className="px-3 py-2 text-right">Em Falta</th>
            <th className="px-3 py-2 text-center">Estado</th>
            <th />
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="text-center text-zinc-500 py-6">Sem adiantamentos</td></tr>}
            {items.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <React.Fragment key={a.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`advance-${a.id}`}>
                    <td className="px-3 py-2 text-zinc-300">{a.date_given}</td>
                    <td className="px-3 py-2 text-white">{a.person}</td>
                    <td className="px-3 py-2 text-zinc-400">{a.purpose || "—"}</td>
                    <td className="px-3 py-2 text-right text-yellow-300 font-medium">{fmt(a.amount_given)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300 font-mono">{fmt(a.justified_total)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300 font-mono">{fmt(a.returned_amount)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${a.outstanding > 0 ? "text-orange-300" : "text-emerald-300"}`}>{fmt(a.outstanding)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.status === "open" ? "bg-yellow-500/15 text-yellow-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                        {a.status === "open" ? "Em aberto" : "Fechado"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setExpandedId(expanded ? null : a.id)} className="text-zinc-300 h-8" title={expanded ? "Fechar detalhes" : "Ver detalhes"} data-testid={`advance-toggle-${a.id}`}>
                        {expanded ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                      </Button>
                      {a.status === "open" && (
                        <Button size="sm" variant="ghost" onClick={() => close(a.id, a.outstanding)} className="text-sky-400 h-8" title="Fechar adiantamento"><Save className="w-4 h-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => del(a.id)} className="text-red-400 h-8"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-white/[0.02] border-t border-white/5">
                      <td colSpan={9} className="px-3 py-3">
                        {(a.justifications || []).length > 0 ? (
                          <ul className="text-xs space-y-1 mb-3">
                            {a.justifications.map((j) => (
                              <li key={j.id} className="text-zinc-400">• <span className="text-white font-mono">{fmt(j.amount)}</span> — {j.description || "(sem descrição)"}</li>
                            ))}
                          </ul>
                        ) : <p className="text-zinc-500 text-xs mb-3">Sem justificações registadas.</p>}
                        {a.status === "open" && (
                          justifyingId === a.id ? (
                            <div className="flex gap-2 flex-wrap items-center">
                              <Input type="number" step="0.01" value={jAmount} onChange={(e) => setJAmount(e.target.value)} placeholder="Valor" className="input-dark h-8 w-28" />
                              <Input value={jDesc} onChange={(e) => setJDesc(e.target.value)} placeholder="Descrição" className="input-dark h-8 flex-1 min-w-[200px]" />
                              <Button size="sm" onClick={() => justify(a.id)} className="bg-emerald-500 hover:bg-emerald-600 h-8">Guardar</Button>
                              <Button size="sm" variant="outline" onClick={() => setJustifyingId(null)} className="h-8">Cancelar</Button>
                            </div>
                          ) : (
                            <Button size="sm" onClick={() => setJustifyingId(a.id)} className="bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 h-8" data-testid={`advance-justify-${a.id}`}>
                              <Plus className="w-3 h-3 mr-1" /> Adicionar justificação
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Movimentos (unificada, editável, filtrável, exportável) ─────────────────
const SOURCE_META = {
  ticket_sale: { label: "Vendas (Bilhetes)", color: "text-violet-300",  bg: "bg-violet-500/10",  border: "border-violet-500/30" },
  income:      { label: "Receitas",       color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  expense:     { label: "Despesas",       color: "text-red-300",     bg: "bg-red-500/10",     border: "border-red-500/30" },
  refund:      { label: "Devoluções",     color: "text-orange-300",  bg: "bg-orange-500/10",  border: "border-orange-500/30" },
  creditor:    { label: "Credores",        color: "text-sky-300",     bg: "bg-sky-500/10",     border: "border-sky-500/30" },
  advance:     { label: "Adiantados",     color: "text-yellow-300",  bg: "bg-yellow-500/10",  border: "border-yellow-500/30" },
};

function MovementsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [srcFilter, setSrcFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [editing, setEditing] = useState(null); // {source, id, description, person, amount, date}

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API_URL}/api/admin/finance/all-movements`, { params, withCredentials: true });
      setRows(data.movements || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a carregar movimentos");
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (srcFilter && r.source !== srcFilter) return false;
      if (!q) return true;
      return (
        (r.description || "").toLowerCase().includes(q) ||
        (r.person || "").toLowerCase().includes(q) ||
        (r.source || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, srcFilter]);

  const totals = useMemo(() => {
    let receitas = 0, vendas = 0, despesas = 0, devolucoes = 0, credores = 0, adiantados = 0;
    for (const r of filtered) {
      const amt = Number(r.amount) || 0;
      if (r.source === "income") receitas += amt;
      else if (r.source === "ticket_sale") vendas += amt;
      else if (r.source === "expense") despesas += amt;
      else if (r.source === "refund") devolucoes += amt;
      else if (r.source === "creditor" && r.status === "pending") credores += amt;
      else if (r.source === "advance" && r.status === "open") {
        adiantados += Number(r.meta?.outstanding ?? amt) || 0;
      }
    }
    const receita_total = receitas + vendas;
    const despesas_liq = despesas - devolucoes;
    const compromissos = credores + adiantados;
    const saldo = receita_total - despesas_liq;
    const saldo_ajustado = saldo - compromissos;
    return { receitas, vendas, despesas, devolucoes, credores, adiantados,
             receita_total, despesas_liq, compromissos, saldo, saldo_ajustado };
  }, [filtered]);

  const del = async (r) => {
    if (!window.confirm(`Eliminar ${SOURCE_META[r.source]?.label || r.source} de ${fmt(r.amount)}?`)) return;
    try {
      const endpoints = {
        income:   `/api/admin/finance/entries/${r.id}`,
        expense:  `/api/admin/finance/entries/${r.id}`,
        refund:   `/api/admin/finance/refunds/${r.id}`,
        creditor: `/api/admin/finance/creditors/${r.id}`,
        advance:  `/api/admin/finance/advances/${r.id}`,
      };
      const url = endpoints[r.source];
      if (!url) { toast.error("Este tipo não pode ser eliminado aqui. Use a aba respetiva."); return; }
      await axios.delete(`${API_URL}${url}`, { withCredentials: true });
      toast.success("Movimento eliminado");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro a eliminar"); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const r = editing;
    try {
      if (r.source === "income" || r.source === "expense") {
        await axios.put(`${API_URL}/api/admin/finance/entries/${r.id}`, {
          description: r.description,
          amount: Number(r.amount) || 0,
          invoice_date: r.date,
          counterparty: r.person,
        }, { withCredentials: true });
      } else if (r.source === "refund") {
        await axios.put(`${API_URL}/api/admin/finance/refunds/${r.id}`, {
          description: r.description,
          amount: Number(r.amount) || 0,
          date: r.date,
          person: r.person,
        }, { withCredentials: true });
      } else if (r.source === "creditor") {
        await axios.put(`${API_URL}/api/admin/finance/creditors/${r.id}`, {
          reason: r.description,
          amount: Number(r.amount) || 0,
          date_incurred: r.date,
          person: r.person,
        }, { withCredentials: true });
      } else if (r.source === "advance") {
        await axios.put(`${API_URL}/api/admin/finance/advances/${r.id}`, {
          purpose: r.description,
          amount_given: Number(r.amount) || 0,
          date_given: r.date,
          person: r.person,
        }, { withCredentials: true });
      } else {
        toast.error("Vendas de bilhetes editam-se em Gestão de Vendas");
        return;
      }
      toast.success("Movimento actualizado");
      setEditing(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro a guardar"); }
  };

  const exportExcel = () => {
    const range = (dateFrom || dateTo) ? `${dateFrom || "início"} a ${dateTo || "hoje"}` : "Total";
    const rows = [
      ["MOVIMENTOS FINANCEIROS", `Período: ${range}`, `${filtered.length} linhas`, `Exportado em ${new Date().toLocaleString("pt-PT")}`],
      [],
      ["Data", "Tipo", "Descrição", "Pessoa/Entidade", "Estado", "Sinal", "Valor (€)"],
      ...filtered.map((r) => [
        r.date || "",
        SOURCE_META[r.source]?.label || r.source,
        r.description || "",
        r.person || "",
        r.status || "",
        r.sign > 0 ? "+" : r.sign < 0 ? "−" : "0",
        Number(r.amount) || 0,
      ]),
      [],
      ["Receitas Manuais", "", "", "", "", "", totals.receitas],
      ["Vendas (Bilhetes)", "", "", "", "", "", totals.vendas],
      ["Receita Total",     "", "", "", "", "", totals.receita_total],
      ["Despesas",          "", "", "", "", "", totals.despesas],
      ["Devoluções",        "", "", "", "", "", totals.devolucoes],
      ["Despesas Líquidas", "", "", "", "", "", totals.despesas_liq],
      ["Credores em Aberto","", "", "", "", "", totals.credores],
      ["Adiantados em Aberto","", "", "", "", "", totals.adiantados],
      ["Compromissos",      "", "", "", "", "", totals.compromissos],
      ["Saldo",             "", "", "", "", "", totals.saldo],
      ["SALDO AJUSTADO",    "", "", "", "", "", totals.saldo_ajustado],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 12 }, { wch: 14 }, { wch: 40 }, { wch: 26 }, { wch: 12 }, { wch: 8 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Movimentos");
    XLSX.writeFile(wb, `movimentos_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const range = (dateFrom || dateTo) ? `${dateFrom || "início"} a ${dateTo || "hoje"}` : "Total";
    doc.setFontSize(14);
    doc.text("Movimentos Financeiros", 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Período: ${range}  ·  ${filtered.length} linhas  ·  ${new Date().toLocaleString("pt-PT")}`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [["Data", "Tipo", "Descrição", "Pessoa/Entidade", "Estado", "Valor (€)"]],
      body: filtered.map((r) => [
        r.date || "",
        SOURCE_META[r.source]?.label || r.source,
        r.description || "",
        r.person || "",
        r.status || "",
        `${r.sign > 0 ? "+" : r.sign < 0 ? "−" : ""}${(Number(r.amount) || 0).toFixed(2)}`,
      ]),
      foot: [
        ["", "", "", "", "Receita Total",     totals.receita_total.toFixed(2)],
        ["", "", "", "", "Despesas Líquidas", totals.despesas_liq.toFixed(2)],
        ["", "", "", "", "Compromissos",      totals.compromissos.toFixed(2)],
        ["", "", "", "", "Saldo Ajustado",    totals.saldo_ajustado.toFixed(2)],
      ],
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [39, 39, 42], textColor: 255 },
      footStyles: { fillColor: [245, 158, 11], textColor: 0, fontStyle: "bold" },
      columnStyles: { 5: { halign: "right" } },
    });
    doc.save(`movimentos_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid="movements-tab">
      {/* Header + totals + exports */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Wallet className="w-6 h-6 text-amber-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Movimentos</h3>
            <p className="text-zinc-500 text-sm">{filtered.length} de {rows.length} movimento(s)</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={exportExcel} className="btn-secondary" data-testid="movements-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button size="sm" onClick={exportPDF} className="btn-secondary" data-testid="movements-export-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-3 grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-zinc-400 text-xs">Procurar</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Descrição, pessoa..." className="input-dark pl-8 h-9" data-testid="movements-search" />          </div>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Tipo</Label>
          <Select value={srcFilter || "__all__"} onValueChange={(v) => setSrcFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="input-dark h-9" data-testid="movements-source-filter"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10">
              <SelectItem value="__all__" className="text-zinc-300">Todos</SelectItem>
              {Object.entries(SOURCE_META).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Desde</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-dark h-9" data-testid="movements-date-from" />
        </div>
        <div>
          <Label className="text-zinc-400 text-xs">Até</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-dark h-9" data-testid="movements-date-to" />
        </div>
      </div>

      {/* Totais (mesma terminologia e ordem das abas) */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg px-3 py-2">
          <p className="text-violet-300 text-[10px] uppercase tracking-wider font-semibold">Vendas (Bilhetes)</p>
          <p className="text-white font-mono text-sm">{fmt(totals.vendas)}</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
          <p className="text-emerald-300 text-[10px] uppercase tracking-wider font-semibold">Receitas</p>
          <p className="text-white font-mono text-sm">{fmt(totals.receitas)}</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          <p className="text-red-300 text-[10px] uppercase tracking-wider font-semibold">Despesas</p>
          <p className="text-white font-mono text-sm">{fmt(totals.despesas)}</p>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
          <p className="text-orange-300 text-[10px] uppercase tracking-wider font-semibold">Devoluções</p>
          <p className="text-white font-mono text-sm">{fmt(totals.devolucoes)}</p>
        </div>
        <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2">
          <p className="text-sky-300 text-[10px] uppercase tracking-wider font-semibold">Credores em Aberto</p>
          <p className="text-white font-mono text-sm">{fmt(totals.credores)}</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
          <p className="text-yellow-300 text-[10px] uppercase tracking-wider font-semibold">Adiantados em Aberto</p>
          <p className="text-white font-mono text-sm">{fmt(totals.adiantados)}</p>
        </div>
        <div className={`rounded-lg px-3 py-2 border ${totals.saldo_ajustado >= 0 ? "bg-amber-500/10 border-amber-500/30" : "bg-red-500/10 border-red-500/30"}`}>
          <p className={`text-[10px] uppercase tracking-wider font-semibold ${totals.saldo_ajustado >= 0 ? "text-amber-300" : "text-red-300"}`}>Saldo Ajustado</p>
          <p className="text-white font-mono text-sm">{fmt(totals.saldo_ajustado)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 border-b border-white/10">
              <tr className="text-left text-zinc-400 text-xs uppercase tracking-wider">
                <th className="px-3 py-2.5">Data</th>
                <th className="px-3 py-2.5">Tipo</th>
                <th className="px-3 py-2.5">Descrição</th>
                <th className="px-3 py-2.5">Pessoa / Entidade</th>
                <th className="px-3 py-2.5">Estado</th>
                <th className="px-3 py-2.5 text-right">Valor</th>
                <th className="px-3 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center text-zinc-500 py-8">A carregar…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-8">Sem movimentos</td></tr>
              )}
              {filtered.map((r, i) => {
                const meta = SOURCE_META[r.source] || { label: r.source, color: "text-zinc-300", bg: "bg-white/5", border: "border-white/10" };
                const isEdit = editing && editing.__key === `${r.source}:${r.id}`;
                const signCls = r.sign > 0 ? "text-emerald-400" : r.sign < 0 ? "text-red-400" : "text-zinc-400";
                const sign = r.sign > 0 ? "+" : r.sign < 0 ? "−" : "";
                return (
                  <tr key={`${r.source}:${r.id}:${i}`} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`movement-row-${r.source}-${r.id}`}>
                    <td className="px-3 py-2 text-zinc-300">
                      {isEdit ? (
                        <Input type="date" value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="input-dark h-8" />
                      ) : (r.date || "—")}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${meta.bg} ${meta.border} ${meta.color}`}>{meta.label}</span>
                    </td>
                    <td className="px-3 py-2 text-white">
                      {isEdit ? (
                        <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="input-dark h-8" />
                      ) : (r.description || "—")}
                    </td>
                    <td className="px-3 py-2 text-zinc-300">
                      {isEdit ? (
                        <Input value={editing.person || ""} onChange={(e) => setEditing({ ...editing, person: e.target.value })} className="input-dark h-8" />
                      ) : (r.person || "—")}
                    </td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">{r.status || "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono ${signCls}`}>
                      {isEdit ? (
                        <Input type="number" step="0.01" value={editing.amount ?? ""} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} className="input-dark h-8 text-right" />
                      ) : (<>{sign}{fmt(r.amount)}</>)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {isEdit ? (
                        <>
                          <Button size="sm" onClick={saveEdit} className="bg-emerald-500 hover:bg-emerald-600 h-8" data-testid={`movement-save-${r.id}`}>
                            <Save className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 h-8"><X className="w-4 h-4" /></Button>
                        </>
                      ) : (
                        <>
                          {r.source !== "ticket_sale" && (
                            <>
                              <Button size="sm" variant="ghost" className="text-zinc-300" onClick={() => setEditing({ ...r, __key: `${r.source}:${r.id}` })} data-testid={`movement-edit-${r.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(r)} data-testid={`movement-delete-${r.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {r.source === "ticket_sale" && (
                            <span className="text-zinc-600 text-xs italic">gerir em Vendas</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main wrapper ─────────────────────────────────────────────────────────────
export const FinanceCenter = () => {
  const [tab, setTab] = useState("summary");
  return (
    <div className="space-y-6" data-testid="finance-center">
      <div>
        <h1 className="text-2xl font-bold text-white">Gestão de Finanças</h1>
        <p className="text-zinc-500 text-sm mt-1">Gerir as Finanças: Receitas, Despesas e Vendas</p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="summary" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tab-summary">Resumo</TabsTrigger>
          <TabsTrigger value="movements" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="tab-movements">Movimentos</TabsTrigger>
          <TabsTrigger value="sales" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white" data-testid="tab-sales">Vendas (Bilhetes)</TabsTrigger>
          <TabsTrigger value="income" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black" data-testid="tab-income">Receitas</TabsTrigger>
          <TabsTrigger value="expense" className="data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-expense">Despesas</TabsTrigger>
          <TabsTrigger value="refunds" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid="tab-refunds">Devoluções</TabsTrigger>
          <TabsTrigger value="creditors" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white" data-testid="tab-creditors">Credores</TabsTrigger>
          <TabsTrigger value="advances" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black" data-testid="tab-advances">Adiantados</TabsTrigger>
        </TabsList>
        <TabsContent value="summary"><SummaryTab /></TabsContent>
        <TabsContent value="movements"><MovementsTab /></TabsContent>
        <TabsContent value="sales"><FinancialSummary /></TabsContent>
        <TabsContent value="income"><EntriesTab kind="income" /></TabsContent>
        <TabsContent value="expense"><EntriesTab kind="expense" /></TabsContent>
        <TabsContent value="refunds"><RefundsTab /></TabsContent>
        <TabsContent value="creditors"><CreditorsTab /></TabsContent>
        <TabsContent value="advances"><AdvancesTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default FinanceCenter;
